import ConditionalWrapper from '@atoms/ConditionalWrapper/ConditionalWrapper';
import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import Text from '@atoms/Text/Text';
import { IImage } from '@models/IImage';
import { INavigation } from '@models/INavigation';
import { INavigationItem } from '@models/INavigationItem';
import { slideTransition } from '@utility/config/animations';
import useMediaQuery from '@utility/hooks/useMediaQuery';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'motion/react';
import { ForwardedRef, MouseEventHandler, forwardRef, useState } from 'react';
import MainNavigation from './MainNavigation';
import SubNavigation from './SubNavigation';
interface Props extends INavigation {
  readonly countrySelectCallback?: MouseEventHandler<HTMLButtonElement>;
  readonly showAnimations: boolean;
}

const Navigation = forwardRef(
  (
    {
      activeCountry,
      countrySelectCallback,
      featuredContent,
      navigation,
      quickLinks,
      showAnimations,
      subNav,
    }: Props,
    ref,
  ) => {
    const [activeSubNav, setActiveSubNav] = useState<INavigationItem[] | null>(
      null,
    );
    const [activeNavItem, setActiveNavItem] = useState<string | null>(null);
    const isMobile = useMediaQuery(752);

    const handleNavSelection = (text: string, children?: INavigationItem[]) => {
      text ? setActiveNavItem(text) : setActiveNavItem(null);
      children?.length ? setActiveSubNav(children) : setActiveSubNav(null);
    };

    const slideDown = {
      animate: {
        y: 0,
      },
      exit: {
        y: '-100%',
      },
    };

    const slideLeft = {
      animate: {
        x: 0,
      },
      exit: {
        x: '-100%',
      },
    };

    const slideRight = {
      animate: {
        x: 0,
      },
      exit: {
        x: '100%',
      },
    };

    const noAnimation = {
      animate: {},
      exit: {},
    };

    return (
      <motion.nav
        ref={ref as ForwardedRef<HTMLDivElement>}
        variants={showAnimations ? slideDown : noAnimation}
        initial="exit"
        animate="animate"
        exit="exit"
        transition={slideTransition}
        key="navigation"
        role="navigation"
        className="tw-absolute tw-top-[56px] tw-z-40 tw-h-[calc(100vh-56px)] tw-max-h-screen tw-w-full tw-overflow-y-scroll tw-bg-white md:tw-top-[64px] md:tw-h-[calc(100vh-64px)] md:tw-max-h-[695px] md:tw-px-[38px] md:tw-py-xl dark:tw-bg-grey-10"
      >
        <div
          className={classNames(
            'tw-container tw-grid tw-h-full tw-w-full tw-gap-md md:tw-h-auto',
            !!quickLinks.length ? 'md:tw-grid-rows-4' : 'md:tw-grid-rows-3',
          )}
        >
          <div className="tw-grid tw-w-full tw-grid-cols-1 tw-divide-grey-20 md:tw-row-span-3 md:tw-grid-cols-3 md:tw-divide-x rtl:md:tw-divide-x-reverse">
            {/* Main Nav/ First Column */}
            <ConditionalWrapper
              condition={isMobile}
              wrapper={(children) => (
                <AnimatePresence initial={false}>
                  {!activeNavItem && !activeSubNav && (
                    <motion.div
                      variants={showAnimations ? slideLeft : noAnimation}
                      initial="exit"
                      animate="animate"
                      exit="exit"
                      transition={slideTransition}
                      key="mainNav"
                    >
                      {children}
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            >
              <div className="rtl:md:tw-pl-x tw-relative tw-flex tw-flex-col tw-justify-between tw-gap-md md:tw-pr-xs">
                {/* Primary Links */}
                <MainNavigation
                  activeNavItem={activeNavItem}
                  items={navigation}
                  type="primary"
                  callback={handleNavSelection}
                  isMobile={isMobile}
                />
                {/* Secondary Links */}
                <MainNavigation
                  activeNavItem={activeNavItem}
                  items={subNav}
                  type="secondary"
                  callback={handleNavSelection}
                  isMobile={isMobile}
                />
              </div>
              {/* Mobile Country Selector */}
              <div className="tw-flex tw-border-b tw-border-grey-20 tw-p-xxs tw-px-[18px] md:tw-hidden md:tw-px-xxs">
                <button
                  type="button"
                  onClick={countrySelectCallback}
                  aria-label="search"
                >
                  {activeCountry && activeCountry.image ? (
                    <img
                      src={activeCountry.image}
                      alt={activeCountry.name}
                      className="tw-h-sm tw-w-sm tw-rounded-full tw-object-cover"
                    />
                  ) : (
                    <Icon name="globe" className="tw-text-2xl" />
                  )}
                </button>
              </div>
            </ConditionalWrapper>

            {/* Mobile Sub Nav */}

            <AnimatePresence>
              {isMobile && activeNavItem && activeSubNav && (
                <motion.div
                  variants={showAnimations ? slideRight : noAnimation}
                  initial="exit"
                  animate="animate"
                  exit="exit"
                  transition={slideTransition}
                  key="subNav"
                  className="tw-absolute tw-inset-0 tw-h-full tw-w-full tw-bg-grey-10"
                >
                  <SubNavigation
                    showAnimations={showAnimations}
                    items={activeSubNav}
                    parent={activeNavItem}
                    onClickCallback={() => {
                      setActiveNavItem(null);
                      setActiveSubNav(null);
                    }}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            {/* For grid spacing and borders */}
            <div />

            {/* Featured Content/Third Column Desktop */}
            <div className="tw-hidden tw-pl-xs md:tw-flex md:tw-items-start rtl:tw-pr-xs">
              <a
                href={featuredContent.linkUrl}
                className="tw-flex tw-flex-col tw-gap-xs"
                onClick={() => {
                  window.dataLayer.push({
                    event: 'trackingEvent_megamenu_clicks',
                    event_name_variable: 'megamenu_clicks',
                    trackingEvent_megamenu_clicks: {
                      megamenu_clicktext: featuredContent.title,
                      subcategory: null,
                      category: null,
                    },
                  });
                }}
              >
                <Image
                  image={
                    {
                      url: featuredContent.image.imageUrl,
                      altText: featuredContent.image.alternativeText,
                    } as IImage
                  }
                />
                <Text
                  variant="sub-headline"
                  className="tw-text-xl tw-font-light tw-text-grey-100"
                >
                  {featuredContent.title}
                </Text>
                <Text
                  variant="body"
                  className="tw-text-sm tw-font-light tw-text-grey-100"
                >
                  {featuredContent.text}
                </Text>
              </a>
            </div>
          </div>

          {/* Bottom Section */}
          {!!quickLinks.length && (
            <ConditionalWrapper
              condition={isMobile}
              wrapper={(children) => (
                <AnimatePresence initial={false}>
                  {!activeNavItem && !activeSubNav && (
                    <motion.div
                      variants={showAnimations ? slideLeft : noAnimation}
                      initial="exit"
                      animate="animate"
                      exit="exit"
                      transition={slideTransition}
                      key="quickLinks"
                    >
                      {children}
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            >
              <div className="tw-grid tw-w-full md:tw-row-span-1 md:tw-w-[calc(33%-32px)]">
                <div className="tw-flex tw-flex-col tw-border-0 tw-border-grey-20 tw-py-xs md:tw-border-t">
                  {quickLinks.map(({ text, url, target }) => (
                    <a
                      key={text}
                      href={url}
                      target={target}
                      className="tw-flex tw-items-center tw-gap-[5px] tw-border-t tw-border-grey-20 tw-px-[18px] tw-py-xxs md:tw-border-none md:tw-px-xxs"
                      onClick={() => {
                        window.dataLayer.push({
                          event: 'trackingEvent_megamenu_clicks',
                          event_name_variable: 'megamenu_clicks',
                          trackingEvent_megamenu_clicks: {
                            megamenu_clicktext: text,
                            subcategory: null,
                            category: null,
                          },
                        });
                      }}
                    >
                      <Text
                        variant="body"
                        className="tw-text-sm tw-text-grey-100"
                      >
                        {text}
                      </Text>
                      {target === '_blank' && (
                        <Icon
                          name="external-link"
                          className="tw-text-[18px] tw-text-grey-70"
                        />
                      )}
                    </a>
                  ))}
                </div>
              </div>
            </ConditionalWrapper>
          )}
        </div>
      </motion.nav>
    );
  },
);
Navigation.displayName = 'Navigation';

export default Navigation;
